import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import { Crawler } from '../../../types/crawler';
import { CrawlerDetail } from '../../../types/crawlerDetail';
import {
  REGISTRATION_STEP,
  RegistrationStep,
} from '../../../constants/registrationStep';

/**
 * クローラーの詳細画面の状態
 */
interface DetailState {
  /** ロード状態 */
  loading: boolean;
  /** 表示中のクローラー */
  selected: Crawler | null;
  /** 表示中のクローラーの最新バージョン */
  selectedDetails: CrawlerDetail[];
  /** 削除確認モーダルの表示状態 */
  isOpenModalDelete: boolean;
  /** 基本情報の更新確認モーダルの表示状態 */
  isOpenModalEdit: boolean;
  /** クローラーバージョン追加モーダルの表示状態 */
  isOpenModalAddDetail: boolean;
  /** クローラーバージョン更新モーダルの表示状態 */
  isOpenModalEditDetail: boolean;
  /** クローラーバージョン削除確認モーダルの表示状態 */
  isOpenModalDeleteDetail: boolean;
  /** バージョン追加ステップ */
  step: RegistrationStep;
  /** APIキーテンプレート不要チェック */
  isCheckUnnecessaryApiKey: boolean;
}

/**
 * クローラー詳細画面の初期状態
 */
const initialState: DetailState = {
  loading: false,
  selected: null,
  selectedDetails: [],
  isOpenModalDelete: false,
  isOpenModalEdit: false,
  isOpenModalAddDetail: false,
  isOpenModalEditDetail: false,
  isOpenModalDeleteDetail: false,
  step: REGISTRATION_STEP.INPUT,
  isCheckUnnecessaryApiKey: false,
};

/**
 * クローラー詳細画面の slice(reducer + action)
 */
export const detailSlice = createSlice({
  name: 'detailCrawler',
  initialState,
  reducers: {
    initialize: () => initialState,
    updateLoadingStatus: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      loading: payload,
    }),
    modalOpened: (
      state,
      { payload }: PayloadAction<DetailState['selected']>,
    ) => ({
      ...state,
      selected: payload,
      // APIキーテンプレートID は 0 以上の整数
      isCheckUnnecessaryApiKey: Number(payload?.apiKeyTemplateId ?? -1) < 0,
    }),
    updateIsOpenModalDelete: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      isOpenModalDelete: payload,
    }),
    updateIsOpenModalEdit: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      isOpenModalEdit: payload,
    }),
    updateIsOpenModalAddDetail: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => ({
      ...state,
      isOpenModalAddDetail: payload,
    }),
    updateIsOpenModalEditDetail: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => ({
      ...state,
      isOpenModalEditDetail: payload,
    }),
    updateIsOpenModalDeleteDetail: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => ({
      ...state,
      isOpenModalDeleteDetail: payload,
    }),
    updateStep: (state, { payload }: PayloadAction<DetailState['step']>) => ({
      ...state,
      step: payload,
    }),
    updateSelectedDetails: (
      state,
      { payload }: PayloadAction<CrawlerDetail[]>,
    ) => ({
      ...state,
      selectedDetails: payload,
    }),
    changeIsCheckUnnecessaryApiKey: (state) => {
      state.isCheckUnnecessaryApiKey = !state.isCheckUnnecessaryApiKey;
    },
  },
});

/**
 * クローラー詳細画面の action
 */
export const detailActions = detailSlice.actions;

/**
 * クローラー詳細画面の selectors
 */
export const detailSelectors = {
  selectedCrawler: (state: RootState) => state.crawler.detail.selected,
  selectedCrawlerDetails: (state: RootState) =>
    state.crawler.detail.selectedDetails,
  isOpenModalDelete: (state: RootState) =>
    state.crawler.detail.isOpenModalDelete,
  isOpenModalEdit: (state: RootState) => state.crawler.detail.isOpenModalEdit,
  isOpenModalAddDetail: (state: RootState) =>
    state.crawler.detail.isOpenModalAddDetail,
  isOpenModalEditDetail: (state: RootState) =>
    state.crawler.detail.isOpenModalEditDetail,
  isOpenModalDeleteDetail: (state: RootState) =>
    state.crawler.detail.isOpenModalDeleteDetail,
  currentModalStep: (state: RootState) => state.crawler.detail.step,
  isCheckUnnecessaryApiKey: (state: RootState) =>
    state.crawler.detail.isCheckUnnecessaryApiKey,
} as const;
